import * as React from 'react';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import FormGenerator from './FormGenerator/FormGenerator';
import {
    finishOnboard,
    getOrganizationalInformation,
    getUserInformation,
    saveOnboarding
} from "../library/api/onboarding";
import { useDispatch } from "react-redux";
import { setAccount } from "../features/account/accountSlice";
import {Stack} from "@mui/material";
import {useTranslation} from "react-i18next";

export default function HorizontalLinearStepper({ 'form': forms }) {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const fetchInitialData = async () => {
        try {
            let initialData = {};
            forms.forEach(form => {
                initialData[form.id] = {};
            });

            const orgInfo = await getOrganizationalInformation();
            if (orgInfo) {
                initialData["organizationInformation"] = orgInfo["organizationInformation"];
            }
            const userInfo = await getUserInformation();
            if (userInfo) {
                initialData["userInformation"] = userInfo["userInformation"];
            }
            setFormData(initialData);
        } catch (error) {
            console.error("Error initializing form data:", error);
        }
    };

    const [activeStep, setActiveStep] = React.useState(0);
    const [skipped, setSkipped] = React.useState(new Set());
    const [formData, setFormData] = React.useState({});
    // console.log("---HorizontalLinearStepper---:", activeStep, "|", JSON.stringify(formData))

    // STEP 1: keep track of field validity per section
    // shape: { [sectionId]: { [keyPath]: "Required" | null } }
    const [invalidBySection, setInvalidBySection] = React.useState({});

    // children (FieldRenderer) will call this to report validity
    const reportValidity = React.useCallback((sectionId, keyPath, isValid, message) => {
        setInvalidBySection(prev => {
            const sec = prev[sectionId] || {};
            const nextSec = { ...sec, [keyPath]: isValid ? null : (message || 'Required') };
            return { ...prev, [sectionId]: nextSec };
        });
    }, []);

    const sectionHasErrors = React.useCallback((sectionId) => {
        const sec = invalidBySection[sectionId];
        return !!sec && Object.values(sec).some(v => v); // any non-null message
    }, [invalidBySection]);

    React.useEffect(() => {
        fetchInitialData();
    }, [activeStep]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleChange = (fieldId, value, parentSection) => {
        setFormData(prevData => {
            const sectionData = { ...(prevData[parentSection] || {}) };
            sectionData[fieldId] = value;
            return {
                ...prevData,
                [parentSection]: sectionData
            };
        });
    };

    const isStepOptional = (step) => {
        // return step === 1;
    };

    const isStepSkipped = (step) => skipped.has(step);

    const handleNext = async (section) => {
        let newSkipped = skipped;
        if (isStepSkipped(activeStep)) {
            newSkipped = new Set(newSkipped.values());
            newSkipped.delete(activeStep);
        }
        switch (activeStep) {
            case 0:
                await saveOnboarding(0, formData[section], true);
                break;
            case 1: {
                await saveOnboarding(1, formData[section]);
                const res = await finishOnboard();
                if (res) {
                    // this will cause user to instantly get redirected to dashboard, handled in Guard.js
                    dispatch(setAccount(res[0]));
                }
                break;
            }
            default:
                break;
        }
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
        setSkipped(newSkipped);
    };

    const handleBack = () => setActiveStep((prevActiveStep) => prevActiveStep - 1);

    const handleSkip = () => {
        if (!isStepOptional(activeStep)) {
            throw new Error("You can't skip a step that isn't optional.");
        }
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
        setSkipped((prevSkipped) => {
            const newSkipped = new Set(prevSkipped.values());
            newSkipped.add(activeStep);
            return newSkipped;
        });
    };

    const activeSectionId = forms[activeStep]?.id;

    return (
        <Box sx={{ width: '100%' }}>
            <Stepper activeStep={activeStep} alternativeLabel>
                {forms.map((field, index) => {
                    const stepProps = {};
                    const labelProps = {};
                    if (isStepOptional(index)) {
                        labelProps.optional = (
                            <Typography variant="caption">Optional</Typography>
                        );
                    }
                    if (isStepSkipped(index)) {
                        stepProps.completed = false;
                    }
                    // STEP 1: mark step label red when that section has errors
                    const stepSectionId = forms[index].id;
                    return (
                        <Step key={field.label} {...stepProps}>
                            <StepLabel {...labelProps} error={sectionHasErrors(stepSectionId)}>
                                Step {index + 1}
                                <div>{t(field.label)}</div>
                            </StepLabel>
                        </Step>
                    );
                })}
            </Stepper>

            {activeStep !== forms.length && (
                <Container maxWidth="md">
                    {/* Header */}
                    <Stack spacing={1.5} sx={{ mt: 4, textAlign: "center" }}>
                        <Typography variant="h5" fontWeight={700}>
                            {t(forms[activeStep].label)}
                        </Typography>
                        {forms[activeStep].description && (
                            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                                {t(forms[activeStep].description)}
                            </Typography>
                        )}
                    </Stack>

                    {/* Form */}
                    <Box sx={{ mt: 3 }}>
                        <FormGenerator
                            parentSection={activeSectionId}
                            fields={forms[activeStep].fields}
                            values={formData}
                            onChange={handleChange}
                            reportValidity={reportValidity}
                        />
                    </Box>

                    {/* Actions */}
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.5}
                        sx={{ pt: 4 }}
                    >
                        <Button
                            variant="contained"
                            color="inherit"
                            onClick={handleBack}
                            disabled={activeStep === 0}
                            sx={{ borderRadius: 999 }}
                        >
                            Back
                        </Button>

                        <Box sx={{ flex: 1 }} />

                        {isStepOptional(activeStep) && (
                            <Button
                                variant="contained"
                                color="inherit"
                                onClick={handleSkip}
                            >
                                Skip
                            </Button>
                        )}

                        <Button
                            variant="contained"
                            onClick={() => handleNext(activeSectionId)}
                            disabled={sectionHasErrors(activeSectionId)}
                            sx={{ borderRadius: 999 }}
                        >
                            {activeStep === forms.length - 1 ? "Finish" : "Next"}
                        </Button>
                    </Stack>
                </Container>
            )}
        </Box>
    );
}
